import { euro, type CatalogBlock, type CatalogPosition, type WebInvoiceLine } from "../../lib/invoices";
import InvoiceTitelFeld from "./InvoiceTitelFeld";

/**
 * Eine Positionszeile im Belegeditor.
 *
 * Aus `InvoicePositionEditor` herausgelöst, als dieser die Blockgruppierung
 * bekam — beides in einer Datei lag über der 300-Zeilen-Grenze aus den
 * Code-Standards, und die Zeile ist ohnehin für sich abgeschlossen.
 */

export type LineRowProps = {
  line: WebInvoiceLine;
  /** Laufende Nummer wie im PDF; bei optionalen Zeilen ungenutzt. */
  nummer: number;
  unitOptions: string[];
  katalogLaeuft: boolean;
  /** Für die Zwischenablage ausgewählt. */
  gewaehlt: boolean;
  onToggleWahl: () => void;
  onChange: (patch: Partial<WebInvoiceLine>) => void;
  onToggleOptional: () => void;
  onDuplicate: () => void;
  onSaveToCatalog: () => void;
  onRemove: () => void;
  /** Eine Katalogposition wurde im Titelfeld gewählt. */
  onKatalogPosition: (item: CatalogPosition) => void;
  /** Ein Baustein wurde im Titelfeld gewählt — er ersetzt diese Zeile. */
  onKatalogBlock: (block: CatalogBlock) => void;
};

const vatValue = (value: number | string): 0 | 7 | 19 => {
  const number = Number(value);
  return number === 0 || number === 7 ? number : 19;
};

export default function InvoiceLineRow({
  line, nummer, unitOptions, katalogLaeuft, gewaehlt,
  onToggleWahl, onChange, onToggleOptional, onDuplicate, onSaveToCatalog, onRemove,
  onKatalogPosition, onKatalogBlock,
}: LineRowProps) {
  const optional = line.line_kind === "optional";
  return (
    <div className={`invoice-position-line${optional ? " optional" : ""}${gewaehlt ? " gewaehlt" : ""}`}>
      <div className="invoice-line-order">
        <label className="invoice-wahl">
          <input
            type="checkbox"
            checked={gewaehlt}
            onChange={onToggleWahl}
            aria-label={`„${line.title || "Position"}" auswählen`}
          />
        </label>
        {/* Keine Pfeile mehr: umsortiert wird im Index (Benjamin, 01.09.2026).
            Zehn Klicks für zehn Stellen waren kein Werkzeug, sondern eine Zumutung. */}
        <strong>{optional ? "Optional" : `Pos. ${nummer}`}</strong>
      </div>
      {/* Eine Zeile Rechenwerte, darunter die Beschreibung über die volle
          Breite. Die Beschreibung stand vorher RECHTS neben dem Titel und
          schob Menge/Einheit/Preis in eine zweite Reihe — beim Erfassen
          liest man aber erst die Position und tippt dann ihre Zahlen. */}
      <label className="invoice-editor-field invoice-position-title">
        <span>Titel / Artikel</span>
        {/* Die Bibliothekssuche sitzt im Titelfeld statt in einem eigenen Feld
            über der Liste: wer eine Position erfasst, tippt ohnehin ihren
            Titel. Wählt er keinen Vorschlag, bleibt es freier Text. */}
        <InvoiceTitelFeld
          wert={line.title}
          onWert={(title) => onChange({ title })}
          onPosition={onKatalogPosition}
          onBlock={onKatalogBlock}
        />
      </label>
      <label className="invoice-editor-field compact pos-menge">
        <span>Menge</span>
        <input
          type="number"
          step="0.01"
          value={line.quantity}
          onChange={(event) => onChange({ quantity: Number(event.target.value) })}
        />
      </label>
      <label className="invoice-editor-field compact pos-einheit">
        <span>Einheit</span>
        <input
          list={`invoice-units-${line.id}`}
          value={line.unit_label}
          onChange={(event) => onChange({ unit_label: event.target.value })}
        />
        <datalist id={`invoice-units-${line.id}`}>
          {unitOptions.map((unit) => <option value={unit} key={unit} />)}
        </datalist>
      </label>
      <label className="invoice-editor-field compact pos-preis">
        <span>Einzel netto</span>
        <div className="invoice-money-input">
          <input
            type="number"
            step="0.01"
            value={line.unit_price_net}
            onChange={(event) => onChange({ unit_price_net: Number(event.target.value) })}
          />
          <b>€</b>
        </div>
      </label>
      <label className="invoice-editor-field compact pos-ust">
        <span>USt.</span>
        <select
          value={line.vat_rate_percent}
          onChange={(event) => onChange({ vat_rate_percent: vatValue(event.target.value) })}
        >
          <option value={19}>19 %</option><option value={7}>7 %</option><option value={0}>0 %</option>
        </select>
      </label>
      <div className="invoice-position-net">
        <span>{optional ? "nicht summiert" : "Netto"}</span>
        <strong>{euro(line.quantity * line.unit_price_net)}</strong>
      </div>
      {line.einkauf_netto != null && line.einkauf_netto > 0 && (
        // Pflanze aus der Bibliothek (16.10.2026, Benjamin: „im konkreten Angebot muss auch möglich
        // sein, die einzelnen Pflanzen individuell zu behandeln“) — nur hier sichtbar, nie im PDF.
        <div className="invoice-pflanze-kalk">
          <span>Pflanze · Einkauf {euro(line.einkauf_netto)} ×</span>
          <input
            type="number" step="0.1" min="1" max="10" inputMode="decimal"
            aria-label="Aufschlag auf den Einkaufspreis"
            value={line.aufschlag ?? ""}
            onChange={(event) => {
              const faktor = Number(event.target.value);
              onChange(faktor > 0
                ? { aufschlag: faktor, unit_price_net: Math.round((line.einkauf_netto || 0) * faktor * 100) / 100 }
                : { aufschlag: null });
            }}
          />
          <span>= {euro(Math.round((line.einkauf_netto || 0) * (line.aufschlag || 0) * 100) / 100)}</span>
          {line.aufschlag && Math.abs(line.unit_price_net - line.einkauf_netto * line.aufschlag) > 0.01 && (
            <small>Preis von Hand: ×{(line.unit_price_net / line.einkauf_netto).toFixed(2).replace(".", ",")}</small>
          )}
        </div>
      )}
      <label className="invoice-editor-field invoice-position-description">
        <span>Beschreibung</span>
        <textarea
          rows={2}
          value={line.description}
          onChange={(event) => onChange({ description: event.target.value })}
          placeholder="Details zur Leistung oder zum Material"
        />
      </label>
      <div className="invoice-line-tools">
        <button
          type="button"
          onClick={onToggleOptional}
          title={optional ? "Wieder mitberechnen" : "Als optionale Position führen"}
        >
          {optional ? "∑" : "o"}
        </button>
        <button type="button" onClick={onDuplicate} title="Position duplizieren">⧉</button>
        <button
          type="button"
          onClick={onSaveToCatalog}
          disabled={katalogLaeuft}
          title="In den Positionskatalog übernehmen"
        >
          ▤
        </button>
        <button
          className="invoice-line-remove"
          type="button"
          onClick={onRemove}
          aria-label="Position entfernen"
        >
          ×
        </button>
      </div>
    </div>
  );
}
